/**
 * Request correlation tracker (issue #1446).
 *
 * Every API response from the backend carries an `X-Request-Id` header set
 * by `RequestIdFilter`. This module captures those ids and exposes them to:
 *   - error toasts / log lines, so user-reported failures map directly to
 *     a server log line via grep,
 *   - the E2E harness, which dumps the ids alongside `[E2E FAIL]` markers
 *     so a failed test gives a clean grep target into `backend-dev.log`.
 *
 * Storage is a small ring buffer in memory. We don't persist — request ids
 * are debugging aids, not identifiers.
 */

const MAX_HISTORY = 200;

interface TrackedRequest {
  id: string;
  url?: string;
  status?: number;
  ts: number;
}

const history: TrackedRequest[] = [];

function pushHistory(entry: TrackedRequest): void {
  history.push(entry);
  if (history.length > MAX_HISTORY) {
    history.splice(0, history.length - MAX_HISTORY);
  }
}

/**
 * Record a request id observed on a response. Call from the API client /
 * Apollo link as soon as the response headers are visible.
 */
export function recordRequestId(id: string | null | undefined, info: { url?: string; status?: number } = {}): void {
  if (!id) return;
  pushHistory({ id, url: info.url, status: info.status, ts: Date.now() });
}

/**
 * The most recent request id observed, or null if none has been seen.
 * Useful for "include the id on the next error toast we render".
 */
function getLastRequestId(): string | null {
  return history.length > 0 ? history[history.length - 1].id : null;
}

/**
 * All request ids in observation order. Used by the E2E harness to dump
 * the correlation ids associated with a failing test.
 */
function getRecentRequestIds(): string[] {
  return history.map((h) => h.id);
}

/**
 * Reset the tracker. Used by the E2E harness between tests so each test's
 * dump only contains its own ids.
 */
function clearRequestIds(): void {
  history.length = 0;
}

const W = typeof window === "undefined" ? null : (window as unknown as Record<string, unknown>);
if (W) {
  // Surface a single global the E2E harness can call via page.evaluate.
  // Keeping it under a __toasthost__ namespace avoids polluting the page.
  const ns = (W.__toasthost__ as Record<string, unknown> | undefined) ?? {};
  ns.getRequestIds = getRecentRequestIds;
  ns.clearRequestIds = clearRequestIds;
  ns.getLastRequestId = getLastRequestId;
  W.__toasthost__ = ns;
}
